import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { SITE_TAB_SECTION_META, type SiteTabSectionId } from './siteTabSectionMeta';

/**
 * SiteTabSectionHeader — the ONE canonical cockpit tab header (Aurora Cockpit
 * `.da-tabhead` system; see src/styles/auroraCockpit.css). Replaces the three
 * hand-rolled header treatments with one token-driven primitive:
 *   • a mono glow-dot eyebrow  "SECTION · CONTEXT"
 *   • a Newsreader display title
 *   • a muted body description
 *   • an actions slot (buttons / chips, right-aligned)
 *   • (hero variant) an optional KPI strip
 *
 * Variants:
 *   • 'section' (default) — the compact header every non-hero tab inherits.
 *   • 'hero'              — the full-bleed atrium banner (Site Home) with the
 *                           corner primary bloom + KPI strip.
 *
 * BACKWARD-COMPATIBLE: every pre-existing prop (icon, title, description,
 * actions, testId, className) is preserved with identical semantics, so the
 * current callers (SupplyChainPanel via SiteTabSectionHeaderFromMeta) keep
 * compiling + rendering unchanged. `variant`, `eyebrow`, `context`, `kpis`,
 * and `location` are purely additive.
 */

/** One KPI cell in the hero KPI strip. */
export interface SiteTabHeaderKpi {
  /** Mono uppercase key label (e.g. "Power target"). */
  label: string;
  /** The value — a big tabular number, or medium display text. */
  value: React.ReactNode;
  /** Optional trailing unit (e.g. "MW IT load"). */
  unit?: string;
  /** Render style: 'num' = big tabular figure, 'text' = medium display text. */
  kind?: 'num' | 'text';
  /** Tint the value with the live accent (--primary). */
  accent?: boolean;
}

export interface SiteTabSectionHeaderProps {
  /** Section icon (lucide). Rendered inside the eyebrow dot row. */
  icon: LucideIcon;
  title: string;
  /**
   * Descriptive subtitle. Accepted for backward-compatibility but NO LONGER
   * RENDERED — William asked for one title and no subtitle line across every
   * tab section. Kept optional so existing callers compile unchanged.
   */
  description?: string;
  /** Header treatment. Defaults to the compact 'section' variant. */
  variant?: 'hero' | 'section';
  /**
   * Mono eyebrow label. Defaults to `title` (uppercased by CSS) so existing
   * callers get a sensible eyebrow with zero changes.
   */
  eyebrow?: string;
  /** Trailing "· CONTEXT" eyebrow segment (e.g. "3 generated", site name). */
  context?: string;
  /** Hero-only location line (e.g. "Calcasieu Parish, LA · 332 ac"). */
  location?: React.ReactNode;
  /** Hero-only KPI strip cells. Ignored by the 'section' variant. */
  kpis?: readonly SiteTabHeaderKpi[];
  /** Right-aligned actions slot (buttons, status pills, chips). */
  actions?: React.ReactNode;
  testId?: string;
  className?: string;
}

export const SiteTabSectionHeader: React.FC<SiteTabSectionHeaderProps> = ({
  icon: Icon,
  title,
  // `description` is intentionally NOT destructured — the subtitle line was
  // removed (William). The prop remains accepted (optional) for compatibility.
  variant = 'section',
  eyebrow,
  context,
  location,
  kpis,
  actions,
  testId,
  className = '',
}) => {
  const isHero = variant === 'hero';
  const TitleTag = (isHero ? 'h1' : 'h2') as 'h1' | 'h2';
  // The big display title is the ONE canonical (accessible) title. The eyebrow
  // is a small mono kicker; when the caller gives no distinct eyebrow it
  // defaults to the title — which rendered the title TWICE (William flagged
  // "Equipment (BOM)" / "Equipment (BOM)"). Only render eyebrow TEXT when it is
  // a genuine, distinct kicker (e.g. "DOCUMENTS"); otherwise keep just the
  // dot + icon accent. The descriptive subtitle line is removed everywhere.
  const eyebrowText = eyebrow ?? title;
  const showEyebrowText = eyebrowText !== title;

  return (
    <header
      className={`da-tabhead ${isHero ? 'da-tabhead--hero' : 'da-tabhead--section'} ${className}`.trim()}
      data-testid={testId}
    >
      <div className="da-tabhead__eyebrow">
        <span className="da-tabhead__dot" aria-hidden />
        <span className="da-type-eyebrow">
          <Icon className="inline-block w-3 h-3 mr-1 align-[-1px]" aria-hidden />
          {showEyebrowText ? eyebrowText : null}
          {context ? (
            <span className="da-tabhead__ctx">{showEyebrowText ? ' · ' : ''}{context}</span>
          ) : null}
        </span>
      </div>

      <div className="da-tabhead__top">
        <div className="da-tabhead__titlewrap">
          <TitleTag className="da-tabhead__title">{title}</TitleTag>
          {isHero && location ? (
            <div className="da-tabhead__loc">{location}</div>
          ) : null}
        </div>
        {actions ? <div className="da-tabhead__actions">{actions}</div> : null}
      </div>

      {isHero && kpis && kpis.length > 0 ? (
        <dl className="da-kpis">
          {kpis.map((kpi, i) => {
            const isText = kpi.kind === 'text';
            const valueClass = isText
              ? `da-kpi__text${kpi.accent ? ' da-kpi__text--accent' : ''}`
              : `da-kpi__num${kpi.accent ? ' da-kpi__num--accent' : ''}`;
            return (
              <div key={`${kpi.label}-${i}`}>
                <dt className="da-kpi__k">{kpi.label}</dt>
                <dd className="da-kpi__v">
                  <span className={valueClass}>{kpi.value}</span>
                  {kpi.unit ? <span className="da-kpi__unit">{kpi.unit}</span> : null}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : null}
    </header>
  );
};

export const SiteTabSectionHeaderFromMeta: React.FC<{
  sectionId: SiteTabSectionId;
  variant?: 'hero' | 'section';
  context?: string;
  location?: React.ReactNode;
  kpis?: readonly SiteTabHeaderKpi[];
  actions?: React.ReactNode;
  className?: string;
}> = ({ sectionId, variant, context, location, kpis, actions, className }) => {
  const meta = SITE_TAB_SECTION_META[sectionId];
  return (
    <SiteTabSectionHeader
      icon={meta.icon}
      title={meta.title}
      description={meta.description}
      variant={variant}
      eyebrow={meta.title}
      context={context}
      location={location}
      kpis={kpis}
      actions={actions}
      className={className}
      testId={`site-tab-section-${sectionId}`}
    />
  );
};
